import { BuilderContent, useIsPreviewing } from '@builder.io/react'
import type { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'

import { FooterBlock } from '@/blocks/Footer'
import { HeaderBlock } from '@/blocks/Header'
import { BlogLayout } from '@/components/BlogLayout'
import { Metatags } from '@/components/Metatags'
import type { ProductKeys } from '@/components/Products/constants'
import { Theme } from '@/components/Theme'
import { BUILDER_PAGE_MODEL } from '@/config/builder'
import { AnalyticsProvider } from '@/core/analytics'
import { BlogProvider } from '@/core/blog'
import type { AnalyticsData } from '@/models'
import { isNotFound } from '@/server/core/builder/pageNotFound'
import { setCacheControlPage } from '@/server/core/builder/setCacheControlPage'
import { getAdsContent } from '@/server/services/builder/ads-content'
import { getBuilderArticlesProps } from '@/server/services/builder/article'
import { getBuilderPageProps } from '@/server/services/builder/page'

import '@/blocks/register'

export async function getServerSideProps(
  { resolvedUrl, req, res }: GetServerSidePropsContext,
  product?: ProductKeys,
) {
  const urlPath = resolvedUrl.split('?').shift() || ''

  const [pageProps, adsBottomContent, mostViewedArticles, latestArticles, othersArticles] =
    await Promise.all([
      getBuilderPageProps({ req, res, urlPath }),
      getAdsContent({ req, res, urlPath, position: 'bottom' }),
      ...getBuilderArticlesProps({ req, res, product }),
    ])

  setCacheControlPage({ page: pageProps.page, res, req })

  return {
    notFound: isNotFound({ page: pageProps.page, req }),
    props: {
      ...pageProps,
      latestArticles: latestArticles || null,
      mostViewedArticles: mostViewedArticles || null,
      othersArticles: othersArticles || null,
      product: product || null,
      adsBottom: adsBottomContent || null,
    },
  }
}

export type BlogProps = InferGetServerSidePropsType<typeof getServerSideProps>

function Blog({ page, footer, ...props }: BlogProps) {
  const isPreviewing = useIsPreviewing()
  const hasPage = page && !isPreviewing

  return (
    <BuilderContent
      model={BUILDER_PAGE_MODEL}
      options={{ includeRefs: true }}
      {...(hasPage && { content: page })}
    >
      {(data, loading, content) => {
        if (loading && !data) {
          return null
        }

        const url = data?.url
        const analytics: AnalyticsData = data?.analytics

        return (
          <Theme data={data}>
            <Metatags data={data} />

            <AnalyticsProvider event={{ ...analytics, url, position: 'topo' }}>
              <BlogProvider>
                <HeaderBlock data={data} />
              </BlogProvider>
            </AnalyticsProvider>

            <AnalyticsProvider event={{ ...analytics, url, position: 'meio' }}>
              <BlogLayout content={content} {...props} />
            </AnalyticsProvider>

            <AnalyticsProvider event={{ ...analytics, url, position: 'rodape' }}>
              <FooterBlock content={footer} />
            </AnalyticsProvider>
          </Theme>
        )
      }}
    </BuilderContent>
  )
}

export default Blog
